import { useCallback, useEffect, useRef, useState } from 'react';
import { Target } from 'lucide-react';
import FormModal from '../FormModal';
import { FormErrorBanner } from '../FormErrorBanner';
import { Spinner } from '../loading';
import { onboardingAPI } from '../../services/api';
import { DEFAULT_PASS_MARK, type ScoreSettings } from '../dashboard/onboardingScore';

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const INVALID_MESSAGE = 'Enter a whole number between 1 and 100.';
const FORM_ID = 'onboarding-pass-mark-form';

/** The server's ScoreSettings, or null when the answer isn't one we can show. */
const toSettings = (raw: unknown): ScoreSettings | null => {
  const r = raw as Partial<ScoreSettings> | null | undefined;
  const passMark = Number(r?.passMark);
  if (!r || !Number.isInteger(passMark) || passMark < 1 || passMark > 100) return null;
  return {
    passMark,
    isDefault: !!r.isDefault,
    updatedBy: r.updatedBy ?? null,
    updatedAt: r.updatedAt ?? null,
  };
};

/** A whole number from 1 to 100, or null. Blank is not 0. */
const parsePassMark = (draft: string): number | null => {
  const trimmed = draft.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 1 && n <= 100 ? n : null;
};

/**
 * Grow → Onboarding Setup → Sign-off Form: the project's onboarding-score pass mark.
 *
 * A compact button beside Create Form shows the current mark ("Pass mark · 70%",
 * hinting "Default" until the project sets its own) and opens a small dialog to
 * change it. The mark only steers the ADVICE on Dashboard → Onboarding (at or
 * above → Ready to deploy, below → Extend); a coordinator still records the call,
 * and decisions already recorded keep the mark they were made against.
 */
export function PassMarkSetting({ projectId }: { projectId: number | null }) {
  const [settings, setSettings] = useState<ScoreSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  // The inline "whole number" message waits until the admin has typed or tried
  // to save, so a blank field after a failed load doesn't open on an error.
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Only the latest read may land, so a slow answer can't overwrite a newer one.
  const readRef = useRef(0);
  const load = useCallback(async (): Promise<ScoreSettings | null> => {
    if (!projectId) return null;
    const ticket = ++readRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await onboardingAPI.getScoreSettings(projectId);
      const next = toSettings(res.data?.data);
      if (ticket !== readRef.current) return null;
      if (next) setSettings(next);
      else setLoadError('Could not load the pass mark.');
      return next;
    } catch (err) {
      if (ticket === readRef.current) setLoadError(apiMessage(err, 'Could not load the pass mark.'));
      return null;
    } finally {
      if (ticket === readRef.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
    return () => { readRef.current += 1; };
  }, [load]);

  if (!projectId) return null;

  const openDialog = () => {
    setDraft(settings ? String(settings.passMark) : '');
    setTouched(false);
    setSaveError(null);
    setOpen(true);
    // After a failed read, try again; the mark fills in unless they've typed one.
    if (!settings) void load().then((s) => { if (s) setDraft((d) => (d === '' ? String(s.passMark) : d)); });
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
  };

  const passMark = parsePassMark(draft);
  const showInvalid = touched && passMark == null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (passMark == null || saving) return;
    if (settings && !settings.isDefault && passMark === settings.passMark) {
      setOpen(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await onboardingAPI.saveScoreSettings({ projectId, passMark });
      // The saved mark is the newest word; a read still in flight must not undo it.
      readRef.current += 1;
      setSettings(toSettings(res.data?.data) ?? { passMark, isDefault: false, updatedBy: null, updatedAt: null });
      setLoading(false);
      setLoadError(null);
      setOpen(false);
    } catch (err) {
      setSaveError(apiMessage(err, 'Could not save the pass mark. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const lastChanged = settings && !settings.isDefault && (settings.updatedBy || settings.updatedAt)
    ? `Last changed${settings.updatedBy ? ` by ${settings.updatedBy}` : ''}${settings.updatedAt
      ? ` on ${new Date(settings.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : ''}.`
    : null;

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        disabled={loading && !settings}
        title={loadError && !settings
          ? `${loadError} Click to try again.`
          : 'The onboarding score a joiner needs to be advised Ready to deploy.'}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60"
      >
        <Target className="h-4 w-4 text-gray-500" aria-hidden />
        <span>Pass mark · {settings ? `${settings.passMark}%` : loading ? '…' : '—'}</span>
        {settings?.isDefault && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase leading-4 tracking-wide text-gray-500">
            Default
          </span>
        )}
      </button>

      <FormModal
        isOpen={open}
        onClose={close}
        title="Onboarding score pass mark"
        size="sm"
        formId={FORM_ID}
        isSubmitting={saving}
        submitLabel={saving ? 'Saving…' : 'Save'}
      >
        {/* noValidate: the inline message below speaks for the field, not the
            browser's own bubble. */}
        <form id={FORM_ID} noValidate onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {saveError && <FormErrorBanner message={saveError} />}
          {!settings && loadError && !loading && <FormErrorBanner message={loadError} />}

          <p id="onboarding-pass-mark-help" className="text-sm text-gray-600">
            A joiner's onboarding score is the average of their buddy's ratings across shadow-training days. At or
            above the pass mark they're advised Ready to deploy; below it, Extend. You still make the final call on
            the Onboarding dashboard.
          </p>

          <div className="space-y-1.5">
            <label htmlFor="onboarding-pass-mark" className="block text-sm font-medium text-gray-700">
              Pass mark
            </label>
            <div className="flex items-center gap-2">
              <input
                id="onboarding-pass-mark"
                type="number"
                min={1}
                max={100}
                step={1}
                inputMode="numeric"
                value={draft}
                disabled={saving}
                onChange={(e) => { setDraft(e.target.value); setTouched(true); setSaveError(null); }}
                aria-invalid={showInvalid}
                aria-describedby={showInvalid ? 'onboarding-pass-mark-error onboarding-pass-mark-help' : 'onboarding-pass-mark-help'}
                className={`w-24 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-50 ${
                  showInvalid
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-100'
                }`}
              />
              <span className="text-sm text-gray-600">%</span>
              {loading && !settings && (
                <span role="status" aria-label="Loading" className="inline-flex">
                  <Spinner />
                </span>
              )}
            </div>
            {showInvalid && (
              <p id="onboarding-pass-mark-error" className="text-xs text-red-600" role="alert">{INVALID_MESSAGE}</p>
            )}
            {settings && (settings.isDefault || lastChanged) && (
              <p className="text-xs text-gray-500">
                {settings.isDefault ? `Using the standard pass mark (${DEFAULT_PASS_MARK}%).` : lastChanged}
              </p>
            )}
          </div>
        </form>
      </FormModal>
    </>
  );
}

export default PassMarkSetting;
